import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Cart, CartItem, Product } from '@/types';
import { cartApi } from '@/services/api';
import toast from 'react-hot-toast';

interface CartState {
  items: CartItem[];
  savedItems: CartItem[];
  totalItems: number;
  totalAmount: number;
  isLoading: boolean;
  isLoadingSaved: boolean;
  error: string | null;
}

const initialState: CartState = {
  items: [],
  savedItems: [],
  totalItems: 0,
  totalAmount: 0,
  isLoading: false,
  isLoadingSaved: false,
  error: null,
};

// Async thunks
export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cartApi.get();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch cart');
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async (itemData: { product_id: number; quantity: number; selected_delivery_option_id: number }, { rejectWithValue }) => {
    try {
      const response = await cartApi.addItem(itemData);
      toast.success('Item added to cart');
      return response.data;
    } catch (error: any) {
      // Don't show toast for 401 errors - interceptor will redirect to login
      if (error?.status_code === 401 || error?.response?.status === 401) {
        return rejectWithValue('Authentication required');
      }
      const message = error.detail || 'Failed to add item to cart';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateCartItem',
  async ({ itemId, quantity }: { itemId: number; quantity: number }, { rejectWithValue }) => {
    try {
      const response = await cartApi.updateItem(itemId, quantity);
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to update cart item';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const increaseCartItem = createAsyncThunk(
  'cart/increaseCartItem',
  async (itemId: number, { rejectWithValue }) => {
    try {
      const response = await cartApi.increaseItem(itemId);
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to increase item quantity';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const decreaseCartItem = createAsyncThunk(
  'cart/decreaseCartItem',
  async (itemId: number, { rejectWithValue }) => {
    try {
      const response = await cartApi.decreaseItem(itemId);
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to decrease item quantity';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (itemId: number, { rejectWithValue }) => {
    try {
      await cartApi.removeItem(itemId);
      toast.success('Item removed from cart');
      return itemId;
    } catch (error: any) {
      const message = error.detail || 'Failed to remove item from cart';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { rejectWithValue }) => {
    try {
      await cartApi.clear();
      toast.success('Cart cleared');
      return [];
    } catch (error: any) {
      const message = error.detail || 'Failed to clear cart';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const saveForLater = createAsyncThunk(
  'cart/saveForLater',
  async (itemId: number, { rejectWithValue }) => {
    try {
      const response = await cartApi.saveForLater(itemId);
      toast.success('Item saved for later');
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to save item for later';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const moveToCart = createAsyncThunk(
  'cart/moveToCart',
  async (itemId: number, { rejectWithValue }) => {
    try {
      const response = await cartApi.moveToCart(itemId);
      toast.success('Item moved to cart');
      return response.data;
    } catch (error: any) {
      const message = error.detail || 'Failed to move item to cart';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const fetchSavedForLater = createAsyncThunk(
  'cart/fetchSavedForLater',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cartApi.getSavedForLater();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to fetch saved items');
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateCartTotals: (state) => {
      state.totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
      state.totalAmount = state.items.reduce((total, item) => {
        return total + (item.product?.price || 0) * item.quantity;
      }, 0);
    },
    addItemToCart: (state, action: PayloadAction<{ product: Product; quantity: number }>) => {
      const { product, quantity } = action.payload;
      const existingItem = state.items.find(item => item.product_id === product.id);
      
      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        state.items.push({
          id: Date.now(), // Temporary ID
          user_id: 0, // Will be set by server
          product_id: product.id,
          product: product,
          quantity: quantity,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
      
      cartSlice.caseReducers.updateCartTotals(state);
    },
    removeItemFromCart: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
      cartSlice.caseReducers.updateCartTotals(state);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Cart
      .addCase(fetchCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.isLoading = false;
        // Filter out saved for later items from regular cart items
        const allItems = action.payload.items || [];
        state.items = allItems.filter((item: CartItem) => !item.is_saved_for_later);
        state.totalItems = action.payload.total_items || 0;
        state.totalAmount = action.payload.total_amount || 0;
        state.error = null;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Add to Cart
      .addCase(addToCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update cart state from response if available
        if (action.payload) {
          const cartData = action.payload;
          if (cartData.items) {
            const allItems = cartData.items || [];
            state.items = allItems.filter((item: CartItem) => !item.is_saved_for_later);
          }
          if (cartData.total_items !== undefined) {
            state.totalItems = cartData.total_items;
          } else if (state.items.length > 0) {
            // Calculate totalItems from items if not provided
            state.totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
          }
          if (cartData.total_amount !== undefined) {
            state.totalAmount = cartData.total_amount;
          } else if (state.items.length > 0) {
            // Calculate totalAmount from items if not provided
            state.totalAmount = state.items.reduce((total, item) => {
              return total + (item.product?.price || 0) * item.quantity;
            }, 0);
          }
        }
        state.error = null;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update Cart Item
      .addCase(updateCartItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the item directly in state to avoid refetching
        const updatedItem = action.payload;
        const index = state.items.findIndex(item => item.id === updatedItem.id);
        if (index !== -1) {
          // Preserve product data from existing item
          const existingItem = state.items[index];
          state.items[index] = {
            ...updatedItem,
            product: existingItem.product, // Preserve product relationship
          };
          // Recalculate totals
          state.totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);
          state.totalAmount = state.items.reduce((sum, item) => sum + (item.price_at_time || item.product?.price || 0) * item.quantity, 0);
        }
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Increase Cart Item
      .addCase(increaseCartItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(increaseCartItem.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the item directly in state to avoid refetching
        const updatedItem = action.payload;
        const index = state.items.findIndex(item => item.id === updatedItem.id);
        if (index !== -1) {
          // Preserve product data from existing item
          const existingItem = state.items[index];
          state.items[index] = {
            ...updatedItem,
            product: existingItem.product, // Preserve product relationship
          };
          // Recalculate totals
          state.totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);
          state.totalAmount = state.items.reduce((sum, item) => sum + (item.price_at_time || item.product?.price || 0) * item.quantity, 0);
        }
      })
      .addCase(increaseCartItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Decrease Cart Item
      .addCase(decreaseCartItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(decreaseCartItem.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the item directly in state to avoid refetching
        const updatedItem = action.payload;
        const index = state.items.findIndex(item => item.id === updatedItem.id);
        if (index !== -1) {
          // Preserve product data from existing item
          const existingItem = state.items[index];
          state.items[index] = {
            ...updatedItem,
            product: existingItem.product, // Preserve product relationship
          };
          // Recalculate totals
          state.totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);
          state.totalAmount = state.items.reduce((sum, item) => sum + (item.price_at_time || item.product?.price || 0) * item.quantity, 0);
        }
      })
      .addCase(decreaseCartItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Remove from Cart
      .addCase(removeFromCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
        cartSlice.caseReducers.updateCartTotals(state);
        state.error = null;
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Clear Cart
      .addCase(clearCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(clearCart.fulfilled, (state) => {
        state.isLoading = false;
        state.items = [];
        state.totalItems = 0;
        state.totalAmount = 0;
        state.error = null;
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Save for Later
      .addCase(saveForLater.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveForLater.fulfilled, (state) => {
        state.isLoading = false;
        // Remove from items and refresh cart
      })
      .addCase(saveForLater.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Move to Cart
      .addCase(moveToCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(moveToCart.fulfilled, (state) => {
        state.isLoading = false;
        // Remove from savedItems and refresh cart
      })
      .addCase(moveToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Saved for Later
      .addCase(fetchSavedForLater.pending, (state) => {
        state.isLoadingSaved = true;
      })
      .addCase(fetchSavedForLater.fulfilled, (state, action) => {
        state.isLoadingSaved = false;
        state.savedItems = action.payload || [];
      })
      .addCase(fetchSavedForLater.rejected, (state) => {
        state.isLoadingSaved = false;
        state.savedItems = [];
      });
  },
});

export const { clearError, updateCartTotals, addItemToCart, removeItemFromCart } = cartSlice.actions;
export { cartSlice };
export default cartSlice.reducer;
